import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Clock, DollarSign, Building, MapPin, FileText } from 'lucide-react';
import Swal from 'sweetalert2';
import { API_URL } from './config'; // Adjust based on your project structure

const CloseShift = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const shiftId = location.pathname.split("/").pop(); // Extracting shiftId from the URL
  const [loading, setLoading] = useState(false);
  const [shiftDetails, setShiftDetails] = useState(null);
  const [totalSales, setTotalSales] = useState(0);
  const [closingRemarks, setClosingRemarks] = useState('');

  useEffect(() => {
    const fetchShiftDetails = async () => {
      setLoading(true);
      const companyId = localStorage.getItem('company_id');
      const branchId = localStorage.getItem('branch_id');
      const operatorId = localStorage.getItem('user_id');
      const shiftStatus = 'open';

      const url = `${API_URL}/shift/getShiftByCompanyBranchOperatorStatus/${companyId}/${branchId}/${operatorId}/${shiftStatus}`;

      try {
        const response = await fetch(url);
        if (!response.ok) {
          throw new Error('Network response was not ok');
        }
        const data = await response.json();

        if (data.length > 0) {
          const shiftData = data[0];
          setShiftDetails(shiftData);
          setTotalSales(shiftData.totalSales || 0); // Assuming totalSales is part of the response
        } else {
          Swal.fire("Info", "No open shifts found.", "info");
          navigate('/openShift'); // Redirect if no open shifts
        }
      } catch (error) {
        console.error('Error fetching shift details:', error);
        Swal.fire("Error", "Failed to fetch shift details.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchShiftDetails();
  }, [navigate]);

  const handleCloseShift = async () => {
    if (!closingRemarks) {
      Swal.fire("Error", "Please enter closing remarks.", "error");
      return;
    }

    setLoading(true);
    const closingData = {
      shiftId: shiftDetails.shift_id,
      closingRemarks: closingRemarks,
      totalSales: totalSales,
      closedAt: new Date().toISOString(),
    };

    try {
      const closeShiftResponse = await fetch(`${API_URL}/shifts/close`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(closingData),
      });

      if (!closeShiftResponse.ok) {
        throw new Error("Failed to close shift");
      }

      const updateShiftResponse = await fetch(`${API_URL}/shifts/update/${shiftDetails.shift_id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          status: "closed",
          date_time_close: new Date().toISOString(),
        }),
      });

      if (!updateShiftResponse.ok) {
        throw new Error("Failed to update shift status");
      }

      Swal.fire("Success", "Shift closed successfully!", "success").then(() => {
        navigate('/pos'); // Redirect to POS after closing shift
      });
    } catch (error) {
      console.error("Error closing shift:", error);
      Swal.fire("Error", "Failed to close shift.", "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div 
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="min-vh-100 bg-light py-5"
    >
      <div className="container">
        <div className="card rounded-3 shadow-sm">
          <div className="card-body">
            <h1 className="card-title text-center">Close Shift</h1>
            <p className="text-center text-muted">Complete your shift and submit final reports</p>

            {loading && (
              <div className="d-flex justify-content-center">
                <div className="spinner-border" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
              </div>
            )}

            {shiftDetails && (
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.2 }}
              >
                <div className="mb-4">
                  <div className="mb-3">
                    <label className="form-label"><Building className="text-gray-400 me-2" size={20} /> Company Details</label>
                    <input
                      type="text"
                      value={localStorage.getItem('companyName') || "REMS ANYTHING"}
                      readOnly
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><MapPin className="text-gray-400 me-2" size={20} /> Branch Information</label>
                    <input
                      type="text"
                      value={localStorage.getItem('branchName') || "Branch: "}
                      readOnly
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><Clock className="text-gray-400 me-2" size={20} /> Shift ID</label>
                    <input
                      type="text"
                      value={shiftDetails.shift_id}
                      readOnly
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><DollarSign className="text-gray-400 me-2" size={20} /> Total Sales</label>
                    <input
                      type="text"
                      value={`${totalSales.toFixed(2)} USD`}
                      readOnly
                      className="form-control"
                    />
                  </div>

                  <div className="mb-3">
                    <label className="form-label"><FileText className="text-gray-400 me-2" size={20} /> Closing Remarks</label>
                    <textarea
                      rows="4"
                      value={closingRemarks}
                      onChange={(e) => setClosingRemarks(e.target.value)}
                      className="form-control"
                      placeholder="Enter your closing remarks..."
                    />
                  </div>
                </div>

                <div className="d-flex justify-content-end">
                  <button
                    type="button"
                    onClick={() => navigate('/pos')}
                    className="btn btn-secondary me-2"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleCloseShift}
                    className="btn btn-danger"
                  >
                    Close Shift
                  </button>
                </div>
              </motion.div>
            )}
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default CloseShift;